import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LandingHeader } from './LandingHeader'
import { LandingFooter } from './LandingFooter'
import { SvgViewer } from './SvgViewer'
import { playgroundUrl } from './playgroundUrl'

// ─── EN sources used across the live embeds ───────────────────
//
// Sources are written one-part-per-line so the editor pane reads
// naturally in a 560px tall embed — actor, do:, needs:, yields:, at:
// each on its own line, blank line between statements.

// Short source for the source + canvas pair. Three actions — enough
// to show a non-trivial flow while keeping the canvas readable at
// embed size.
const smallSource = `customer
do: place order
needs: cart, payment method
yields: order
at: web

payment service
do: authorize payment
needs: order
yields: authorized order, charge id
at: payments

warehouse
do: ship order
needs: authorized order, shipping label
yields: shipped order
at: fulfillment`

// Minimal source that still triggers subsystem discovery. Verified
// via mcp__endiagram__structure — below 8 actions the engine treats
// the whole thing as one cluster; at 8 with these four natural
// groupings, it partitions into intake / auth-refund / settle /
// fulfillment. Short names keep the editor pane readable.
const subsystemSource = `customer
do: place order
needs: cart, payment method
yields: order
at: web

fraud
do: score
needs: order, history
yields: risk score
at: payments

payment
do: authorize
needs: order
yields: authorized, charge id
at: payments

payment
do: capture
needs: authorized, risk score
yields: paid
at: payments

warehouse
do: pick
needs: paid
yields: picked
at: fulfillment

warehouse
do: ship
needs: picked, label
yields: shipped
at: fulfillment

notifier
do: email
needs: paid, address
yields: receipt
at: comms

support
do: refund
needs: shipped, complaint, charge id
yields: refunded
at: support`

// ─── Claude Code transcript ────────────────────────────────────
//
// Real session captured against the MCP. Tool call arguments and
// responses are the actual JSON — abbreviated for readability but
// every key/value comes from a real staging API call.

type Role = 'user' | 'assistant' | 'tool-call' | 'tool-result'
interface Turn {
    role: Role
    content: string
}

const ROLE_STYLE: Record<Role, { label: string; color: string }> = {
    'user':        { label: 'you',       color: '#cba6f7' },
    'assistant':   { label: 'claude',    color: '#89dceb' },
    'tool-call':   { label: 'tool',      color: '#89b4fa' },
    'tool-result': { label: 'output',    color: '#a6e3a1' },
}

// Real MCP JSON outputs, pretty-printed. Trimmed to the top-level
// keys worth reading — nothing synthesised.
const jsonStructure = `{
  "shape": "Complex Topology",
  "actionCount": 8,
  "entityCount": 15,
  "actorCount": 6,
  "locusCount": 5,
  "depth": 12,
  "criticalPathLength": 13,
  "maxParallelism": 6,
  "bridgeNodes": [
    "order", "paid order", "place order", "score risk",
    "ship package", "send receipt", "process refund"
  ],
  "subsystems": [
    { "name": "Subsystem 1", "stages": ["place order", "score risk"] },
    { "name": "Subsystem 2", "stages": ["authorize payment", "process refund"] },
    { "name": "Subsystem 3", "stages": ["capture payment", "send receipt"] },
    { "name": "Subsystem 4", "stages": ["pick items", "ship package"] }
  ],
  "minCuts": [
    {
      "cutSize": 1,
      "affectedPairs": 12,
      "nodes": ["place order", "process refund", "score risk",
                "send receipt", "ship package"]
    }
  ]
}`

const jsonLive = `{
  "isStructurallyLive": false,
  "siphonCount": 7,
  "coveredSiphonCount": 2,
  "uncoveredSiphons": [
    { "entities": ["customer complaint"], "affectedActors": ["support"] },
    { "entities": ["payment method"],     "affectedActors": ["customer"] },
    { "entities": ["shipping label"],     "affectedActors": ["warehouse"] },
    {
      "entities": ["customer history", "risk score"],
      "affectedActors": ["fraud service", "payment service"]
    },
    {
      "entities": [
        "authorized order", "cart", "order",
        "paid order", "picked package", "shipped order"
      ],
      "affectedActors": [
        "customer", "fraud service", "notifier",
        "payment service", "support", "warehouse"
      ]
    }
  ],
  "isStructurallyBounded": false
}`

const jsonReachable = `{
  "from": "cart",
  "to": "refunded",
  "reachable": true,
  "distance": 6,
  "path": [
    { "id": "cart",                 "role": "SOURCE" },
    { "id": "action:place order",   "role": "JOIN",  "actor": "customer",        "location": "web" },
    { "id": "order",                "role": null },
    { "id": "action:authorize payment", "role": "FORK", "actor": "payment service", "location": "payments" },
    { "id": "charge id",            "role": null },
    { "id": "action:process refund", "role": "JOIN", "actor": "support",         "location": "support" },
    { "id": "refunded",             "role": null }
  ]
}`

const transcript: Turn[] = [
    { role: 'user', content: "Black Friday's in 3 weeks. Audit this orders + returns flow for anything that'd break under 10x load." },
    { role: 'assistant', content: "Starting with topology — want to see the shape and single points of failure before anything else." },
    { role: 'tool-call', content: 'mcp__endiagram__structure({ source: checkout.en, detect_findings: true })' },
    { role: 'tool-result', content: jsonStructure },
    { role: 'assistant', content: "7 bridge nodes on an 8-action flow — almost every step is a choke point. `score risk` sits on the capture path, so fraud scoring is a hard dependency for every order, not a sidecar check. Checking liveness next: I want to know what happens when inputs dry up." },
    { role: 'tool-call', content: 'mcp__endiagram__live({ source: checkout.en })' },
    { role: 'tool-result', content: jsonLive },
    { role: 'assistant', content: "Second uncovered siphon is the important one — `{customer history, risk score}`. If fraud service stalls, risk score dries up, capture payment needs it but can't fire. The card is authorized (customer is charged) but no confirmation goes out. That's \"my card was charged but I got nothing\" support tickets at scale. Let me check whether there's any refund route that doesn't go through a shipped order first." },
    { role: 'tool-call', content: 'mcp__endiagram__reachable({ source, from: "paid order", to: "refunded" })' },
    { role: 'tool-result', content: jsonReachable },
    { role: 'assistant', content: "The shortest path to `refunded` still goes through `ship package`. There's no pre-ship cancel route. If warehouse oversells (they will on Black Friday) and shipping label generation fails, the customer is charged with no modeled way back." },
    { role: 'assistant', content: "Three things to flag before the sale:\n\n1. Fraud service is a silent chokepoint holding customer money hostage. Add a timeout + fallback policy (auto-approve low-risk / auto-reject with auth release) or you ship support tickets at scale.\n\n2. No refund path for paid-but-not-shipped orders. Add a pre-ship cancel action that takes `paid order` + `charge id` directly, before inventory oversells hit.\n\n3. `charge id` persistence is implicit. It's produced at auth, not consumed until refund — nothing carries it forward, so in code it has to be looked up. Make that write-to-orders-table explicit at capture time or the first refund incident debugs a lookup that isn't modeled." },
]

// ─── Terminal component ────────────────────────────────────────

function TerminalBlock({ title, turns }: { title: string; turns: Turn[] }) {
    return (
        <div
            className="rounded-xl overflow-hidden"
            style={{
                border: '1px solid rgba(205, 214, 244, 0.08)',
                background: '#1e1e2e',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
        >
            <div
                className="flex items-center gap-2 px-4 py-2.5"
                style={{
                    borderBottom: '1px solid rgba(205, 214, 244, 0.06)',
                    background: '#181825',
                }}
            >
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
                </div>
                <span className="ml-2 text-[11px] font-mono" style={{ color: '#6c7086' }}>
                    {title}
                </span>
            </div>
            <div
                className="px-5 py-5 font-mono text-[11.5px] sm:text-[12.5px] leading-[1.75]"
                style={{ color: '#cdd6f4' }}
            >
                {turns.map((turn, i) => {
                    const style = ROLE_STYLE[turn.role]
                    const isJson = turn.role === 'tool-result'
                    const isCall = turn.role === 'tool-call'
                    return (
                        <div
                            key={i}
                            className="grid"
                            style={{
                                gridTemplateColumns: '5.5em minmax(0, 1fr)',
                                columnGap: '0.75em',
                                marginBottom: i === turns.length - 1 ? 0 : '1em',
                            }}
                        >
                            <span
                                style={{
                                    color: style.color,
                                    fontSize: '0.78em',
                                    fontWeight: 700,
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    paddingTop: '0.2em',
                                    opacity: 0.9,
                                    userSelect: 'none',
                                }}
                            >
                                {style.label}
                            </span>
                            <div
                                style={{
                                    minWidth: 0,
                                    overflowX: isJson ? 'auto' : 'visible',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: isJson ? 'normal' : 'break-word',
                                    color: isCall ? style.color
                                         : isJson ? style.color
                                         : turn.role === 'user' ? style.color
                                         : '#cdd6f4',
                                    fontWeight: turn.role === 'user' ? 600 : 400,
                                    opacity: isJson ? 0.92 : 1,
                                }}
                            >
                                {turn.content}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ─── Live playground pane embed ────────────────────────────────
//
// Iframes a single pane of the deployed playground (canvas, editor, or
// structure findings) via the `?view=<pane>&code=<source>` URL params.
// Each iframe shows one component with real inputs — not the whole
// playground app duplicated five times on one page. Lazy-loaded so
// opening the page doesn't cold-start every embed at once.

type Pane = 'canvas' | 'editor' | 'structure' | 'split-canvas' | 'split-structure'

function LivePlayground({ source, pane = 'canvas', caption, height = '520px' }: {
    source: string
    pane?: Pane
    caption: string
    height?: string
}) {
    const embedSrc = `${playgroundUrl}/?view=${pane}&code=${encodeURIComponent(source)}`
    const fullSrc = `${playgroundUrl}/?code=${encodeURIComponent(source)}`
    const isSplit = pane === 'split-canvas' || pane === 'split-structure'
    return (
        <figure
            className="rounded-xl overflow-hidden flex flex-col"
            style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}
        >
            <div
                className="flex items-center gap-2 px-4 py-2"
                style={{ borderBottom: '1px solid var(--border-subtle)' }}
            >
                <span
                    className="text-[10px] font-mono uppercase tracking-widest"
                    style={{ color: 'var(--accent)' }}
                >
                    {pane}
                </span>
                <span className="text-[10px] font-mono text-text-muted">·</span>
                <span className="text-[10px] font-mono text-text-muted">live</span>
                <a
                    href={fullSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-[10px] font-mono text-accent hover:underline"
                    title="Open full playground with this source"
                >
                    open in playground ↗
                </a>
            </div>
            {/* For split modes both panes are inside one iframe (so they
                share the zustand store — editing source updates the right
                pane live). A 2-col CSS grid wrapper with a gap column
                creates the visual separation without touching playground
                code: two identical iframes would duplicate the pointer/
                zoom state; a vertical overlay divider at 50% does it
                without duplication. */}
            <div
                className="relative"
                style={{ background: 'var(--surface)' }}
            >
                <iframe
                    src={embedSrc}
                    title={caption}
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                    referrerPolicy="no-referrer-when-downgrade"
                    style={{
                        display: 'block',
                        width: '100%',
                        height,
                        border: 0,
                        background: 'var(--bg)',
                    }}
                />
                {isSplit && (
                    /* Mobile only: 8px horizontal gutter between the stacked
                       panes. Desktop intentionally has no gutter — any line
                       at 50% bisects the playground's fixed-centered modals
                       (e.g. Import EN) and we can't reach into the iframe
                       to hide the overlay when those open. */
                    <div
                        aria-hidden
                        className="block md:hidden"
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 'calc(50% - 4px)',
                            height: '8px',
                            background: 'var(--surface)',
                            pointerEvents: 'none',
                        }}
                    />
                )}
            </div>
            <figcaption className="px-4 py-3 text-xs text-text-secondary leading-relaxed">
                {caption}
            </figcaption>
        </figure>
    )
}

// ─── Author avatar ─────────────────────────────────────────────
//
// Tries `/dushyant.png` first; if it 404s, swaps to a clean SVG
// silhouette on an accent disk. The state-driven swap avoids the
// ugly broken-image glyph and never shows the letter "DS" — just a
// real photo or a proper placeholder, nothing in between.

function AuthorAvatar() {
    // Candidates tried in order. Local /dushyant.png ships in the
    // repo so production doesn't depend on a third-party avatar
    // service. The unavatar.io URLs are a backstop in case the local
    // file gets out of sync; a silhouette SVG is the final floor.
    const CANDIDATES = [
        '/dushyant.png',
        'https://unavatar.io/x/TENNarrates',
        'https://unavatar.io/github/dushyant30suthar',
    ]
    const [idx, setIdx] = useState(0)
    const [failed, setFailed] = useState(false)
    return (
        <div
            className="shrink-0 w-16 h-16 rounded-full overflow-hidden flex items-center justify-center"
            style={{ background: failed ? 'color-mix(in srgb, var(--accent) 20%, var(--surface))' : 'var(--surface)' }}
        >
            {failed ? (
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: 'var(--accent)' }} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="9" r="3.5" />
                    <path d="M5 20c0-3.866 3.134-7 7-7s7 3.134 7 7" />
                </svg>
            ) : (
                <img
                    src={CANDIDATES[idx]}
                    alt="Dushyant Suthar"
                    className="w-full h-full object-cover"
                    onError={() => {
                        if (idx + 1 < CANDIDATES.length) setIdx(idx + 1)
                        else setFailed(true)
                    }}
                />
            )}
        </div>
    )
}

// ─── Topology card — SvgViewer with dark/light toggle in its topbar ──
//
// The theme toggle is passed to SvgViewer via `topbarTrailing`, so the
// card's chrome (label + toggle) all lives inside one frame. No extra
// label row above the viewer — the viewer's own topbar carries it.

function TopologyCard({
    label, darkSrc, lightSrc,
}: {
    label: string
    darkSrc: string
    lightSrc: string
}) {
    const [isDark, setIsDark] = useState(true)
    const toggle = (
        <button
            type="button"
            onClick={() => setIsDark(d => !d)}
            className="w-6 h-6 flex items-center justify-center rounded text-text-muted hover:text-text transition-colors"
            style={{ border: '1px solid var(--border-subtle)', background: 'var(--surface)' }}
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            title={isDark ? 'Switch to light' : 'Switch to dark'}
        >
            {isDark ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
            ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3A7 7 0 0 0 21 12.79z" />
                </svg>
            )}
        </button>
    )
    return (
        <SvgViewer
            src={isDark ? darkSrc : lightSrc}
            label={label}
            alt={`${label} topology example`}
            maxHeight="340px"
            topbarTrailing={toggle}
        />
    )
}

// ─── Page ──────────────────────────────────────────────────────

export function ShowcasePage() {
    const location = useLocation()
    useEffect(() => {
        // Footer uses react-router <Link to="/showcase#contact">. React Router
        // does not scroll to the hash on its own — it only drives history. The
        // requestAnimationFrame gives the page one paint so the target <section>
        // is in the DOM before scrollIntoView fires.
        if (!location.hash) return
        const id = location.hash.slice(1)
        requestAnimationFrame(() => {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
    }, [location.hash])
    return (
        <div className="min-h-screen bg-bg" style={{ scrollBehavior: 'smooth' }}>
            <LandingHeader />
            <main className="pt-32 pb-24 px-4 sm:px-6">
                <div className="max-w-5xl mx-auto">

                    {/* Hero */}
                    <div className="mb-20 max-w-3xl">
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">
                            Showcase
                        </p>
                        <h1 className="font-display text-3xl md:text-5xl font-bold leading-[1.1] mb-6">
                            What a structural<br />
                            <span className="text-accent">audit feels like.</span>
                        </h1>
                        <p className="text-text-secondary leading-relaxed">
                            One production-shaped system, analyzed two ways &mdash; from an agent
                            that picks the right tool for each question, and from a browser-side
                            workspace where the same graph is on a canvas.
                        </p>
                    </div>

                    {/* Claude Code transcript */}
                    <section className="mb-24">
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-3">
                            Inside an agent
                        </p>
                        <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                            A Black Friday audit, unedited.
                        </h2>
                        <p className="text-text-secondary leading-relaxed mb-8 max-w-2xl">
                            A senior engineer asks for a structural audit before a traffic spike.
                            Claude picks the right tool for each question &mdash; topology first,
                            then liveness, then path reachability &mdash; and lands on three
                            findings worth shipping.
                        </p>
                        <TerminalBlock
                            title="claude code · mcp-endiagram"
                            turns={transcript}
                        />
                    </section>

                    {/* Source + canvas — single iframe, shared store */}
                    <section className="mb-24">
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-3">
                            Write. See.
                        </p>
                        <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                            Type the system. Watch it render.
                        </h2>
                        <p className="text-text-secondary leading-relaxed mb-10 max-w-2xl">
                            Names shared between actions auto-wire the graph. No explicit edges,
                            no dangling references, no drag-and-drop layout &mdash; the shape
                            falls out of the declared dependencies.
                        </p>
                        <LivePlayground
                            pane="split-canvas"
                            source={smallSource}
                            height="560px"
                            caption="Three actions, fully wired from name-sharing alone."
                        />
                    </section>

                    {/* Source + structure — single iframe, shared store */}
                    <section className="mb-24">
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-3">
                            Write. Analyze.
                        </p>
                        <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                            Type the system. Read the structure.
                        </h2>
                        <p className="text-text-secondary leading-relaxed mb-10 max-w-2xl">
                            The findings pane groups actions into natural subsystems, lists
                            bridges every flow passes through, and flags cycles.
                        </p>
                        <LivePlayground
                            pane="split-structure"
                            source={subsystemSource}
                            height="620px"
                            caption="Edit the source &mdash; the subsystem map, bridges, and cycles move with it."
                        />
                    </section>

                    {/* Topology SVGs via SvgViewer — same controls as landing */}
                    <section className="mb-24">
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-3">
                            Topology classification
                        </p>
                        <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                            Six shapes, labelled automatically.
                        </h2>
                        <p className="text-text-secondary leading-relaxed mb-10 max-w-2xl">
                            The engine classifies a system by the shape of its needs/yields
                            graph, and the renderer picks a layout that fits &mdash; linear
                            for pipelines, branching for trees, diamond-aware for fork-joins,
                            loop-aware for state machines, isolated-cluster-aware for
                            disconnected components, and a mixed layout for the{' '}
                            <em className="text-accent not-italic">complex topologies</em>{' '}
                            most real-world systems actually are.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            <TopologyCard
                                label="pipeline"
                                darkSrc="/showcase/topology-pipeline-dark.svg"
                                lightSrc="/showcase/topology-pipeline-light.svg"
                            />
                            <TopologyCard
                                label="tree"
                                darkSrc="/showcase/topology-tree-dark.svg"
                                lightSrc="/showcase/topology-tree-light.svg"
                            />
                            <TopologyCard
                                label="fork-join"
                                darkSrc="/showcase/topology-forkjoin-dark.svg"
                                lightSrc="/showcase/topology-forkjoin-light.svg"
                            />
                            <TopologyCard
                                label="state machine"
                                darkSrc="/showcase/topology-statemachine-dark.svg"
                                lightSrc="/showcase/topology-statemachine-light.svg?v=2"
                            />
                            <TopologyCard
                                label="disconnected"
                                darkSrc="/showcase/topology-disconnected-dark.svg"
                                lightSrc="/showcase/topology-disconnected-light.svg"
                            />
                            <TopologyCard
                                label="complex"
                                darkSrc="/showcase/topology-complex-dark.svg"
                                lightSrc="/showcase/topology-complex-light.svg"
                            />
                        </div>
                    </section>

                    {/* Contact — author + channels in one card, no duplication */}
                    <section id="contact" style={{ scrollMarginTop: '96px' }}>
                        <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-3">
                            Contact
                        </p>
                        <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                            Get in touch.
                        </h2>
                        <p className="text-text-secondary leading-relaxed mb-8 max-w-2xl">
                            Bugs, feature requests, questions, anything in between.
                        </p>
                        <div
                            className="flex flex-row items-center gap-4 sm:gap-6 p-5 sm:p-6 rounded-xl max-w-3xl"
                            style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}
                        >
                            <AuthorAvatar />
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-text text-base">Dushyant Suthar</p>
                                <p className="text-xs text-text-muted mt-0.5 leading-relaxed">
                                    Building EN &mdash; deterministic structural analysis for
                                    concurrent systems.
                                </p>
                                <ul className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-xs">
                                    <li>
                                        <a
                                            href="https://github.com/dushyant30suthar"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-accent hover:underline"
                                        >
                                            github
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://x.com/TENNarrates"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-accent hover:underline"
                                        >
                                            x
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://www.linkedin.com/in/dushyant30suthar/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-accent hover:underline"
                                        >
                                            linkedin
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="mailto:privacy@endiagram.com"
                                            className="font-mono text-accent hover:underline"
                                        >
                                            privacy@endiagram.com
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>
                </div>
            </main>
            <LandingFooter />
        </div>
    )
}
